var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { usePowerPanel, usePowerPanelContext } from 'shared/features/power-panel';
import { Text, Stack, Box, Flex, Icon, Heading, Spinner } from 'shared/components';
import { useEffect, useRef, useState } from 'react';
import { ManageMedicationIcon } from 'shared/assets/icons';
import { useMemberAllergies, useActiveAllergyContext, useMedicationsPrescribeIFrame, useCanIEditPrescriptions, } from 'shared/features/erx';
import { formatFullDate } from 'shared/utils';
import { useMember } from 'shared/hooks';
import { TrackedButton, TrackedComponentView } from '../../analytics';
var PowerPanelBody = function (_a) {
    var _b, _c, _d;
    var activeAllergyProp = _a.activeAllergy, memberUserId = _a.memberUserId, changeActiveAllergy = _a.changeActiveAllergy;
    var onClose = usePowerPanelContext().onClose;
    var _e = useState(activeAllergyProp), activeAllergy = _e[0], setActiveAllergy = _e[1];
    var _f = useMemberAllergies(memberUserId), allergiesResponse = _f.data, isLoading = _f.isLoading, isRefetching = _f.isRefetching;
    var allergyIndex = activeAllergy
        ? ((_b = allergiesResponse === null || allergiesResponse === void 0 ? void 0 : allergiesResponse.allergies) === null || _b === void 0 ? void 0 : _b.findIndex(function (a) { return a.medication === (activeAllergy === null || activeAllergy === void 0 ? void 0 : activeAllergy.medication); })) || 0
        : null;
    useEffect(function () {
        if (activeAllergyProp !== null) {
            setActiveAllergy(activeAllergyProp);
        }
    }, [activeAllergyProp]);
    useEffect(function () {
        var _a;
        if (activeAllergy != null) {
            var mappedActiveAllergy = (_a = allergiesResponse === null || allergiesResponse === void 0 ? void 0 : allergiesResponse.allergies) === null || _a === void 0 ? void 0 : _a.find(function (a) { return a.medication === (activeAllergy === null || activeAllergy === void 0 ? void 0 : activeAllergy.medication); });
            if (mappedActiveAllergy) {
                setActiveAllergy(mappedActiveAllergy);
            }
            else {
                // If allergies refresh and the selected one no longer exists (ie was removed), close the power panel
                onClose();
            }
        }
    }, [allergiesResponse]);
    var prevDisabled = allergyIndex == 0;
    var prevColor = prevDisabled ? 'gray.500' : 'green.500';
    var nextDisabled = allergyIndex == (((_c = allergiesResponse === null || allergiesResponse === void 0 ? void 0 : allergiesResponse.allergies) === null || _c === void 0 ? void 0 : _c.length) || 1) - 1;
    var nextColor = nextDisabled ? 'gray.500' : 'green.500';
    var incrementActiveAllergy = function () {
        if (allergiesResponse && allergyIndex !== null && !nextDisabled) {
            setActiveAllergy(allergiesResponse.allergies[allergyIndex + 1]);
            changeActiveAllergy(1);
        }
    };
    var decrementActiveAllergy = function () {
        if (allergiesResponse && allergyIndex !== null && !prevDisabled) {
            setActiveAllergy(allergiesResponse.allergies[allergyIndex - 1]);
            changeActiveAllergy(-1);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(TrackedComponentView, { spring_doc_id: "compass046", component_name: "Allergies Power Panel" }), _jsxs(Stack, __assign({ spacing: 4 }, { children: [_jsxs(Box, { children: [_jsx(Text, __assign({ size: "body3", fontWeight: 'bold' }, { children: "Med. name" })), _jsx(Heading, __assign({ size: 'h3', fontWeight: 'bold' }, { children: activeAllergy === null || activeAllergy === void 0 ? void 0 : activeAllergy.medication })), (isLoading || isRefetching) && _jsx(Spinner, {}), false && ( // This AC is being revisited, hiding for now
                            _jsxs(Flex, { children: [_jsx(Text, __assign({ color: prevColor, paddingRight: 1, onClick: decrementActiveAllergy, cursor: prevDisabled ? 'inherit' : 'pointer', userSelect: "none" }, { children: "Prev" })), _jsx(Text, __assign({ paddingRight: 1 }, { children: "/" })), _jsx(Text, __assign({ color: nextColor, onClick: incrementActiveAllergy, cursor: nextDisabled ? 'inherit' : 'pointer', userSelect: "none" }, { children: "Next" }))] }))] }), _jsxs(Box, { children: [_jsx(Text, __assign({ size: "body3", fontWeight: 'bold' }, { children: "Reaction" })), _jsx(Text, __assign({ size: "body3" }, { children: activeAllergy === null || activeAllergy === void 0 ? void 0 : activeAllergy.reaction }))] }), _jsxs(Box, { children: [_jsx(Text, __assign({ size: "body3", fontWeight: 'bold' }, { children: "Onset" })), _jsx(Text, __assign({ size: "body3" }, { children: formatFullDate((_d = activeAllergy === null || activeAllergy === void 0 ? void 0 : activeAllergy.onset) !== null && _d !== void 0 ? _d : '') }))] })] }))] }));
};
var PowerPanelFooter = function (_a) {
    var memberId = _a.memberId, memberUserId = _a.memberUserId, appointmentId = _a.appointmentId;
    var open = useMedicationsPrescribeIFrame(memberId, memberUserId, appointmentId).open;
    var canEditPrescriptions = useCanIEditPrescriptions();
    return (_jsx(_Fragment, { children: canEditPrescriptions && (_jsx(TrackedButton, __assign({ eventName: "Manage Allergies Button", eventProperties: { spring_doc_id: 'compass043' }, leftIcon: _jsx(Icon, { as: ManageMedicationIcon }), "data-testid": "manageAllergiesButton", onClick: open, w: "100%" }, { children: "Manage Allergies" }))) }));
};
export var useAllergyPowerPanel = function (memberId, appointmentId) {
    var _a, _b, _c;
    var _d = usePowerPanelContext(), activeId = _d.activeId, contextOnClose = _d.onClose, setContent = _d.setContent, setFooter = _d.setFooter;
    var _e = useActiveAllergyContext(), activeAllergy = _e.activeAllergy, setActiveAllergy = _e.setActiveAllergy;
    var member = useMember(memberId !== null && memberId !== void 0 ? memberId : '').data;
    var allergiesResponse = useMemberAllergies((_a = member === null || member === void 0 ? void 0 : member.user.id) !== null && _a !== void 0 ? _a : '').data;
    var changeActiveAllergy = function (iterator) {
        if (activeAllergy && allergiesResponse) {
            // Get index of current active allergy, move index around visible allergies +-1 depending on button prev/next button press
            var currentAllergyIndex = allergiesResponse.allergies.indexOf(activeAllergy);
            // if goes to ends, do nothing
            if (currentAllergyIndex + iterator > allergiesResponse.allergies.length - 1 ||
                currentAllergyIndex + iterator < 0)
                return;
            setActiveAllergy(allergiesResponse.allergies[currentAllergyIndex + iterator]);
        }
    };
    useEffect(function () {
        var _a;
        if (activeId === 'allergy') {
            setContent(_jsx(PowerPanelBody, { activeAllergy: activeAllergy, changeActiveAllergy: changeActiveAllergy, memberUserId: (_a = member === null || member === void 0 ? void 0 : member.user.id) !== null && _a !== void 0 ? _a : '' }));
        }
    }, [activeAllergy]);
    var onClose = useRef(function () {
        setActiveAllergy(null);
    }).current;
    var powerPanel = usePowerPanel({
        id: 'allergy',
        header: 'Medication Allergies',
        content: (_jsx(PowerPanelBody, { activeAllergy: activeAllergy, changeActiveAllergy: changeActiveAllergy, memberUserId: (_b = member === null || member === void 0 ? void 0 : member.user.id) !== null && _b !== void 0 ? _b : '' })),
        footer: (_jsx(PowerPanelFooter, { memberId: memberId !== null && memberId !== void 0 ? memberId : '', memberUserId: (_c = member === null || member === void 0 ? void 0 : member.user.id) !== null && _c !== void 0 ? _c : '', appointmentId: appointmentId })),
        dismissOnNav: function (pathname) { return !pathname.includes('/client/'); },
        onClose: onClose,
    });
    return powerPanel;
};
